"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef } from "react";

/*
Currently only dragging along the x-axis is supported, but it should be easy
  to expand to also support the y-axis, if needed.
 */
export const useClickAndDrag = (element, onDrag, onDragStart, onDragEnd, t0) => {
  const $ = _c(13);
  const threshold = t0 === undefined ? 5 : t0;
  const initialXRef = useRef(0);
  const isDraggingRef = useRef(false);
  const isMousePressedRef = useRef(false);
  const preventClickRef = useRef(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = event => {
      if (event.button === 0) {
        event.preventDefault();
        initialXRef.current = event.clientX;
        isMousePressedRef.current = true;
        preventClickRef.current = false;
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleMouseDown = t1;
  let t2;
  if ($[1] !== onDrag || $[2] !== onDragStart || $[3] !== threshold) {
    t2 = event_0 => {
      const mouseX = event_0.clientX;
      if (isDraggingRef.current) {
        event_0.preventDefault();
        const totalMovedX = initialXRef.current - mouseX;
        onDrag(totalMovedX);
      } else {
        if (isMousePressedRef.current && Math.abs(mouseX - initialXRef.current) > threshold) {
          isDraggingRef.current = true;
          preventClickRef.current = true;
          initialXRef.current = mouseX;
          if (onDragStart) {
            onDragStart(mouseX);
          }
        }
      }
    };
    $[1] = onDrag;
    $[2] = onDragStart;
    $[3] = threshold;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleMouseMove = t2;
  let t3;
  if ($[5] !== onDragEnd) {
    t3 = event_1 => {
      isMousePressedRef.current = false;
      if (isDraggingRef.current) {
        event_1.preventDefault();
        isDraggingRef.current = false;
        if (onDragEnd) {
          onDragEnd();
        }
      }
    };
    $[5] = onDragEnd;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleMouseUp = t3;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = event_2 => {
      if (preventClickRef.current) {
        event_2.preventDefault();
        preventClickRef.current = false;
      }
    };
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleClick = t4;
  let t5;
  let t6;
  if ($[8] !== element || $[9] !== handleMouseMove || $[10] !== handleMouseUp) {
    t5 = () => {
      if (element) {
        element.addEventListener("mousedown", handleMouseDown);
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
        document.addEventListener("click", handleClick);
        return () => {
          element.removeEventListener("mousedown", handleMouseDown);
          document.removeEventListener("mousemove", handleMouseMove);
          document.removeEventListener("mouseup", handleMouseUp);
          document.removeEventListener("click", handleClick);
        };
      }
    };
    t6 = [element, handleMouseDown, handleMouseMove, handleMouseUp, handleClick];
    $[8] = element;
    $[9] = handleMouseMove;
    $[10] = handleMouseUp;
    $[11] = t5;
    $[12] = t6;
  } else {
    t5 = $[11];
    t6 = $[12];
  }
  useEffect(t5, t6);
};
export const useClickAndDragScroll = (element, onDragScrollStart, onDragScrollEnd, threshold) => {
  const $ = _c(14);
  const speedTimeRef = useRef(0);
  const speedXRef = useRef(0);
  const lastTotalMovedX = useRef(0);
  const scrollLeftPosRef = useRef(0);
  const timeoutRef = useRef(undefined);
  const preventClickRef = useRef(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = totalMovedX => {
      const now = Date.now();
      const time = now - speedTimeRef.current;
      const currentSpeed = totalMovedX - lastTotalMovedX.current;
      const addPreviousSpeed = time < 300 && (currentSpeed === 0 || currentSpeed > 0 && speedXRef.current > 0 || currentSpeed < 0 && speedXRef.current < 0);
      const speedX = currentSpeed + (addPreviousSpeed ? speedXRef.current : 0);
      speedTimeRef.current = now;
      speedXRef.current = speedX;
      lastTotalMovedX.current = totalMovedX;
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const updateSpeed = t0;
  let t1;
  if ($[1] !== element?.scrollLeft || $[2] !== onDragScrollStart) {
    t1 = mouseX => {
      scrollLeftPosRef.current = element?.scrollLeft ?? 0;
      lastTotalMovedX.current = mouseX;
      if (onDragScrollStart) {
        onDragScrollStart();
      }
    };
    $[1] = element?.scrollLeft;
    $[2] = onDragScrollStart;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const onDragStart = t1;
  let t2;
  if ($[4] !== element) {
    t2 = totalMovedX_0 => {
      element?.scrollTo({
        left: scrollLeftPosRef.current + totalMovedX_0,
        behavior: "instant"
      });
      updateSpeed(totalMovedX_0);
    };
    $[4] = element;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const onDrag = t2;
  let t3;
  if ($[6] !== element || $[7] !== onDragScrollEnd) {
    t3 = () => {
      if (Date.now() - speedTimeRef.current < 50) {
        const speedXFactor = speedXRef.current / 30;
        const onTimeout = count => {
          const scroll = speedXFactor * (count / 50) ** 3;
          if (count > 0 && Math.abs(scroll) >= 1) {
            element?.scrollBy({
              left: scroll,
              behavior: "instant"
            });
            timeoutRef.current = setTimeout(onTimeout, 5, count - 1);
          } else {
            timeoutRef.current = undefined;
          }
        };
        onTimeout(50);
      }
      if (onDragScrollEnd) {
        onDragScrollEnd();
      }
    };
    $[6] = element;
    $[7] = onDragScrollEnd;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const onDragEnd = t3;
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = event => {
      if (timeoutRef.current) {
        event.preventDefault();
        clearTimeout(timeoutRef.current);
        timeoutRef.current = undefined;
        preventClickRef.current = true;
      } else {
        if (preventClickRef.current) {
          preventClickRef.current = false;
        }
      }
    };
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const handleMouseDown = t4;
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = event_0 => {
      if (preventClickRef.current) {
        event_0.preventDefault();
        preventClickRef.current = false;
      }
    };
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const handleClick = t5;
  useClickAndDrag(element, onDrag, onDragStart, onDragEnd, threshold);
  let t6;
  let t7;
  if ($[11] !== element) {
    t6 = () => {
      if (element) {
        element.addEventListener("mousedown", handleMouseDown);
        element.addEventListener("click", handleClick);
        return () => {
          element.removeEventListener("mousedown", handleMouseDown);
          element.removeEventListener("click", handleClick);
        };
      }
    };
    t7 = [element, handleMouseDown, handleClick];
    $[11] = element;
    $[12] = t6;
    $[13] = t7;
  } else {
    t6 = $[12];
    t7 = $[13];
  }
  useEffect(t6, t7);
};